import { useEffect, useRef } from 'react'
import ReactMap, { MapRef, ViewState } from 'react-map-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import {
  Coordinates,
  DEFAULT_DURATION,
  DEFAULT_HEIGHT,
  DEFAULT_WIDTH,
  DEFAULT_ZOOM,
  getInitialViewState,
  Pin,
  Pins,
  Properties,
  CircleLayer
} from 'components/Map'

type Location = {
  latitude: number
  longitude: number
  radius: number
}

interface MapProps {
  width?: number | string
  height?: number | string
  features?: GeoJSON.FeatureCollection<GeoJSON.Point, Properties>
  theme?: string
  coordinates?: Coordinates | undefined | null
  selected?: GeoJSON.Position
  expectedLocation?: Location
  fillColor?: string
  fillOpacity?: number
}

export const Map: React.FC<MapProps> = ({
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  features,
  theme = 'light',
  coordinates,
  selected,
  expectedLocation,
  fillColor,
  fillOpacity
}) => {
  const mapStyle = theme === 'dark' ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11'
  const mapRef = useRef<MapRef>(null)

  const initialViewState: Partial<ViewState> = coordinates ? { ...coordinates, zoom: 17 } : getInitialViewState(features)

  useEffect(() => {
    if (!mapRef.current) return
    mapRef.current.resize()
  }, [width, height, selected])

  useEffect(() => {
    if (!selected) return
    const [longitude, latitude] = selected
    mapRef.current?.flyTo({ center: [longitude, latitude], zoom: DEFAULT_ZOOM, duration: DEFAULT_DURATION })
  }, [selected])

  return (
    <ReactMap reuseMaps ref={mapRef} initialViewState={initialViewState} style={{ width, height }} mapStyle={mapStyle}>
      {coordinates ? <Pin coordinates={coordinates} /> : null}
      {features ? <Pins data={features} /> : null}
      {expectedLocation ? <CircleLayer coordinates={expectedLocation} radius={expectedLocation.radius} opacity={fillOpacity} fillColor={fillColor} /> : null}
    </ReactMap>
  )
}
